/* eslint-disable react-hooks/exhaustive-deps */
import React, {useState, useRef, useEffect} from 'react'
import SvgCard from './SvgCard'
import './card.css'
import Checkbox from '../helpers/Checkbox'
import {Form} from 'react-bootstrap'
import {Flow, IsPublishStep} from '../editors/WalkthroughSteps'

function Card(props: any, ref: any) {
  // These props are required to display the Card as a standalone component
  const {marker, containerheight} = props
  const {disableEditing} = props
  const svgCardRef = disableEditing ? props.svgCardRef : props.compilerProps.svgCardRef

  // These props require the context of Compile.tsx
  const currCard = props?.compilerProps?.currCard
  const defaultQrScale = props?.compilerProps?.defaultQrScale
  const handleMarkerChange = props?.compilerProps?.handleMarkerChange
  const handleImageSelect = props?.compilerProps?.handleImageSelect
  const isAdmin = props?.compilerProps?.isAdmin
  const currStep = props?.compilerProps?.currStep
  const flow = props?.compilerProps?.flow
  const isExclusivelyQrMarker = props?.compilerProps?.isExclusivelyQrMarker

  let baseScale = 900
  if (marker?.type === 'pc') {
    baseScale = 1800
  } else if (marker?.type === 'qr') {
    baseScale = 1800
  } else if (marker?.type === 'pc2') {
    baseScale = 2400
  } else if (marker?.type === 'pc3') {
    baseScale = 3300
  } else if (marker?.type === 'custom' && marker?.customDimensions) {
    // Calculate baseScale based on custom dimensions to maintain quality
    // Use larger dimension as reference and scale appropriately
    const {width, height} = marker.customDimensions
    const maxDimension = Math.max(width, height)
    // Scale factor to ensure good quality regardless of size
    baseScale = Math.max(1200, Math.min(3000, Math.round(maxDimension * 150)))
  }

  const getRatio = () => marker?.ratio || 1

  const getCardWidth = () => {
    if (marker?.type === 'qr' || marker?.type === 'custom') {
      const ratio = getRatio()
      // For portrait images (ratio < 1), reduce width to prevent height overflow
      if (ratio < 1) {
        // Scale width down proportionally to the aspect ratio
        // For very portrait images (ratio 0.5), use 35% width
        // For slightly portrait images (ratio 0.8), use ~56% width
        return `${Math.max(10, 40 * ratio)}%`
      }
      return '100%'
    }
    return '100%'
  }

  const isDragging = useRef(false)
  const [offset, setOffset] = useState({x: 0, y: 0})
  const [isMobile, setIsMobile] = useState(false);

  const [qrScale, setQrScale] = useState(baseScale * (marker?.qr?.scale || defaultQrScale))
  const [qrcodePosition, setQrcodePosition] = useState({x: -999, y: -999})

  useEffect(() => {
    //this code guarantees proper initialization of qrcodePosition based on default values
    //because x or y will never be -999, this can only run once
    if (qrcodePosition.x === -999 && qrcodePosition.y === -999) {
      // Get QR position or calculate smart default based on marker type
      let defaultX = 0
      let defaultY = 0

      if (marker?.qr?.position) {
        defaultX = marker.qr.position[0]
        defaultY = marker.qr.position[1]
      } 
      console.log('baseScale', baseScale)
      console.log('defaultX', defaultX)
      console.log('defaultY', defaultY)

      setQrcodePosition({
        x: baseScale * defaultX,
        y: (baseScale * defaultY) / getRatio(),
      })
      
    }

    setIsMobile(window.innerWidth <= 800);
  }, [marker, baseScale])

  const handleShowQr = () => {
    let newMarker = {...marker}
    newMarker.qr.visible = !newMarker.qr.visible
    handleMarkerChange(newMarker)
  }

  const handleMouseDown = (e, svgRef) => {
    if (e.target.id === 'qrcode') {
      const svg = svgRef.current[marker._id]
      const point = svg.createSVGPoint()
      point.x = e.clientX
      point.y = e.clientY
      const cursorPoint = point.matrixTransform(svg.getScreenCTM().inverse())

      setOffset({
        x: cursorPoint.x - qrcodePosition.x,
        y: cursorPoint.y - qrcodePosition.y,
      })

      isDragging.current = true
    }
  }

  const handleMouseMove = (e, svgRef, isTouch = false) => {
    if (isDragging.current) {
      const svg = svgRef.current[marker._id]
      const point = svg?.createSVGPoint()

      if (isTouch) {
        e.preventDefault() //disables mobile scrolling while touching QR code
        point.x = e.touches[0].clientX
        point.y = e.touches[0].clientY
      } else {
        point.x = e.clientX
        point.y = e.clientY
      }

      const cursorPoint = point.matrixTransform(svg.getScreenCTM().inverse())
      let newX = cursorPoint.x - offset.x
      let newY = cursorPoint.y - offset.y

      // Ensure the qrcode element stays within the bounds
      newX = Math.max(0, Math.min(newX, baseScale - qrScale))
      newY = Math.max(0, Math.min(newY, baseScale / getRatio() - qrScale))

      setQrcodePosition({x: newX, y: newY})
    }
  }

  const handleMouseUp = () => {
    if (isDragging.current) {
      isDragging.current = false
      handleMarkerChange({
        'qr.position': [qrcodePosition.x / baseScale, qrcodePosition.y / (baseScale / getRatio())],
      })
    }
  }

  const handleTouchStart = (e, svgRef) => handleMouseDown(e.touches[0], svgRef)
  const handleTouchMove = (e, svgRef) => handleMouseMove(e, svgRef, true)

  useEffect(() => {
      //only allow precise QR controls for admin
      window.addEventListener('mousemove', (e) => handleMouseMove(e, svgCardRef))
      window.addEventListener('mouseup', handleMouseUp)

      window.addEventListener('touchmove', (e) => handleTouchMove(e, svgCardRef), {passive: false})
      window.addEventListener('touchend', handleMouseUp)

      return () => {
        window.removeEventListener('mousemove', (e) => handleMouseMove(e, svgCardRef))
        window.removeEventListener('mouseup', handleMouseUp)

        window.removeEventListener('touchmove', (e) => handleTouchMove(e, svgCardRef))
        window.removeEventListener('touchend', handleMouseUp)
      }
    
  }, [offset, qrcodePosition, qrScale])

  const handleQuadrantButton = (quadrantName) => {
    const updatePosition = (newX, newY) => {
      setQrcodePosition({x: newX, y: newY})
      handleMarkerChange({'qr.position': [newX / baseScale, newY / (baseScale / getRatio())]})
    }

    let padding = 70
    let maxWidth = baseScale - qrScale - padding
    let maxHeight = baseScale / getRatio() - qrScale - padding

    switch (quadrantName) {
      case 'UpperLeft':
        updatePosition(padding, padding)
        break
      case 'UpperRight':
        updatePosition(maxWidth, padding)
        break
      case 'LowerLeft':
        updatePosition(padding, maxHeight)
        break
      case 'LowerRight':
        updatePosition(maxWidth, maxHeight)
        break
    }
  }

  const handleSliderChange = (e) => {
    const newScale = baseScale * e.target.value

    // Ensure the QR code stays within the bounds after scaling
    let newX = qrcodePosition.x
    let newY = qrcodePosition.y

    if (newX + newScale > baseScale) {
      newX = baseScale - newScale
    }

    if (newY + newScale > baseScale / getRatio()) {
      newY = baseScale / getRatio() - newScale
    }

    setQrScale(newScale)
    setQrcodePosition({x: newX, y: newY})

    handleMarkerChange({
      'qr.scale': newScale / baseScale,
      'qr.position': [newX / baseScale, newY / (baseScale / getRatio())],
    })
  }

  // Content Display Filters

  const showControls = () =>
    !marker?.protected?.isActive && marker?.qr?.visible && !isExclusivelyQrMarker()
  const showSliderControls = () => showControls() && isAdmin
  const showQuadrantButtons = () =>
    showControls() && (!IsPublishStep(currStep) || flow === Flow.repeat)
  const showDetachedQrCode = () => marker?.protected?.isActive && !marker?.qr?.visible
  const showQrCodeToggle = () => marker && !marker?.qr?.required

  // Shared Class Names

  const quadStyleTop = {fontSize: '24px', padding: '0px', marginBottom: '-5px'}
  const quadStyleBottom = {fontSize: '24px', padding: '0px', marginTop: '-5px'}
  const quad = 'col m-1 p-0 btn btn-primary btn-sm text-center'
  const inputLabel = 'col-2 p-2 m-auto text-center fs-3 fw-bold'
  const svgCardStyle = {
    //backgroundColor prevents a thin white line from appearing when image is displayed
    //width: getCardWidth(),
    //maxWidth: '450px',
    margin: '15px auto',
    width:  `${(containerheight -130) * getRatio()}px`,
    maxWidth: isMobile ? 'calc(-130px + 90vw)' : 'none', // Prevent overflow on mobile with extra padding
    backgroundColor: marker.imageUrl ? '#777777' : '',
  }

  return (
    <>
      {marker && ( //always show the SVG
        <div className='card-shadowbox-mb m-auto' style={svgCardStyle}>
          <div id='svg-card-container' style={{position: 'relative', width: '100%', height: '100%', margin: '0 auto'}}>
            <SvgCard
              ref={ref}
              svgCardRef={svgCardRef}
              refMarker={marker}
              baseScale={baseScale}
              getRatio={getRatio}
              qrcodePosition={qrcodePosition}
              handleMouseDown={handleMouseDown}
              handleTouch={handleTouchStart}
            />
          </div>
        </div>
      )}

      {!disableEditing && (
        <>
            <div className='row mt-5'>
              <div className={inputLabel}>Scale QR</div>
              <div className='col-10 p-2'>
                <Form.Range
                  step='any'
                  min={Math.max(0.05, 200 / baseScale)}
                  max={baseScale / getRatio() / baseScale}
                  value={qrScale / baseScale}
                  onChange={handleSliderChange}
                  className='h-100'
                />
              </div>
            </div>
          

          {showDetachedQrCode() && (
            <div className='row'>
              <div className='col-2'></div>
              <div className={inputLabel}>QR Code</div>
              <div className='col-5 p-2'>
                <svg width='100%'>
                  <image x={0} y={0} width='100%' height='100%' xlinkHref={marker?.qr?.image} />
                </svg>
              </div>
              <div className='col-2'></div>
            </div>
          )}

          {
            showQrCodeToggle() ? (
              isAdmin ? (
                <Checkbox
                  isChecked={marker?.qr?.visible}
                  text='Include QR Code'
                  doCheck={handleShowQr}
                />
              ) : (
                <></>
              ) //show nothing when card is active for non-admin user
            ) : (
              <></>
            ) //show nothing qr is required
          }
        </>
      )}
    </>
  )
}

export default React.forwardRef(Card)
